/**
 * useResourceOptions Hook
 *
 * Aggregates data from multiple resource hooks to provide
 * a unified interface for the SmartResourceBuilder component.
 *
 * This hook fetches all available resources (volumes, secrets, connections, etc.)
 * and transforms them into a format suitable for resource selection dropdowns.
 */

import { useMemo } from 'react';
import { useVolumes } from '../../volumes/hooks/useVolumes';
import { useSecrets } from '../../secrets/hooks/useSecrets';
import { useDatabaseConnections } from '../../database-connections/hooks/useDatabaseConnections';
import { useDocumentDatabases } from '../../document-databases/hooks/useDocumentDatabases';
import { useManagedSqlDbs } from '../../managed-sql-databases/hooks/useManagedSqlDbs';
import { useApplications } from '../../applications/hooks/useApplications';

/**
 * Resource option for dropdown selection
 */
export interface ResourceOption {
  code: string;
  name: string;
  id?: string;
}

/**
 * Resource type for the smart resource builder
 */
export type ResourceType =
  | 'all' // "*" - all resources
  | 'application' // "app:{code}/function:*" or "application:{code}"
  | 'volume' // "volume:{code}"
  | 'secret' // "secret:{code}"
  | 'connection' // "connection:{code}"
  | 'documentdb' // "documentdb:{code}"
  | 'managed-db' // "managed-db:{code}"
  | 'ai' // "ai:{modelAlias}"
  | 'custom'; // Custom pattern

/**
 * Resource type configuration
 */
export interface ResourceTypeConfig {
  type: ResourceType;
  label: string;
  description: string;
  icon: string;
  patternPrefix: string;
  patternSuffix?: string;
}

/**
 * Resource type configurations
 */
export const RESOURCE_TYPE_CONFIGS: ResourceTypeConfig[] = [
  {
    type: 'all',
    label: 'All Resources',
    description: 'Access to all resources of this type',
    icon: '🌐',
    patternPrefix: '*',
  },
  {
    type: 'application',
    label: 'Application',
    description: 'Application and its functions',
    icon: '📱',
    patternPrefix: 'app:',
    patternSuffix: '/function:*',
  },
  {
    type: 'volume',
    label: 'Volume',
    description: 'Storage volume for files',
    icon: '📁',
    patternPrefix: 'volume:',
  },
  {
    type: 'secret',
    label: 'Secret',
    description: 'Secret credentials or API keys',
    icon: '🔐',
    patternPrefix: 'secret:',
  },
  {
    type: 'connection',
    label: 'Database Connection',
    description: 'External database connection',
    icon: '🔗',
    patternPrefix: 'connection:',
  },
  {
    type: 'documentdb',
    label: 'Document Database',
    description: 'NoSQL document database',
    icon: '📄',
    patternPrefix: 'documentdb:',
  },
  {
    type: 'managed-db',
    label: 'Managed SQL Database',
    description: 'Managed PostgreSQL database',
    icon: '🗃️',
    patternPrefix: 'managed-db:',
  },
  {
    type: 'ai',
    label: 'AI Model',
    description: 'AI model for chat completions',
    icon: '🤖',
    patternPrefix: 'ai:',
  },
  {
    type: 'custom',
    label: 'Custom Pattern',
    description: 'Enter a custom resource pattern',
    icon: '✏️',
    patternPrefix: '',
  },
];

/**
 * Get resource type config by type
 */
export function getResourceTypeConfig(type: ResourceType): ResourceTypeConfig | undefined {
  return RESOURCE_TYPE_CONFIGS.find((c) => c.type === type);
}

/**
 * Parse a resource pattern to extract its type and code
 */
export function parseResourcePattern(pattern: string): { type: ResourceType; code: string } | null {
  if (pattern === '*') {
    return { type: 'all', code: '*' };
  }

  // Application pattern: app:{code}/function:*
  const appMatch = pattern.match(/^app:([^/]+)\/function:(.*)$/);
  if (appMatch) {
    return { type: 'application', code: appMatch[1] };
  }

  // Application pattern for tenant permissions: application:{code}
  const applicationMatch = pattern.match(/^application:(.+)$/);
  if (applicationMatch) {
    return { type: 'application', code: applicationMatch[1] };
  }

  // Volume pattern: volume:{code}
  const volumeMatch = pattern.match(/^volume:(.+)$/);
  if (volumeMatch) {
    return { type: 'volume', code: volumeMatch[1] };
  }

  // Secret pattern: secret:{code}
  const secretMatch = pattern.match(/^secret:(.+)$/);
  if (secretMatch) {
    return { type: 'secret', code: secretMatch[1] };
  }

  // Connection pattern: connection:{code}
  const connectionMatch = pattern.match(/^connection:(.+)$/);
  if (connectionMatch) {
    return { type: 'connection', code: connectionMatch[1] };
  }

  // Document DB pattern: documentdb:{code}
  const documentDbMatch = pattern.match(/^documentdb:(.+)$/);
  if (documentDbMatch) {
    return { type: 'documentdb', code: documentDbMatch[1] };
  }

  // Managed DB pattern: managed-db:{code}
  const managedDbMatch = pattern.match(/^managed-db:(.+)$/);
  if (managedDbMatch) {
    return { type: 'managed-db', code: managedDbMatch[1] };
  }

  // AI model pattern: ai:{modelAlias}
  const aiMatch = pattern.match(/^ai:(.+)$/);
  if (aiMatch) {
    return { type: 'ai', code: aiMatch[1] };
  }

  // Unknown pattern - treat as custom
  return { type: 'custom', code: pattern };
}

/**
 * Build a resource pattern from type and code
 */
export function buildResourcePattern(type: ResourceType, code: string): string {
  const config = getResourceTypeConfig(type);
  if (!config) return code;

  if (type === 'all') return '*';
  if (type === 'custom') return code;

  const suffix = config.patternSuffix || '';
  return `${config.patternPrefix}${code}${suffix}`;
}

/**
 * Get human-readable label for a resource pattern
 */
export function getResourcePatternLabel(pattern: string): string {
  if (pattern === '*') return 'All Resources';

  const parsed = parseResourcePattern(pattern);
  if (!parsed) return pattern;

  const config = getResourceTypeConfig(parsed.type);
  if (!config || parsed.type === 'custom') return pattern;

  return `${config.label}: ${parsed.code}`;
}

/**
 * Options returned by useResourceOptions hook
 */
export interface ResourceOptionsResult {
  applications: ResourceOption[];
  volumes: ResourceOption[];
  secrets: ResourceOption[];
  connections: ResourceOption[];
  documentDbs: ResourceOption[];
  managedDbs: ResourceOption[];
  isLoading: boolean;
  isError: boolean;
}

/**
 * Hook for fetching all available resources for the smart resource builder
 * @param tenantId - Tenant ID
 */
export function useResourceOptions(tenantId: string): ResourceOptionsResult {
  // Fetch all resource types in parallel
  const { data: applicationsData, isLoading: appsLoading, isError: appsError } = useApplications(tenantId);
  const { data: volumesData, isLoading: volumesLoading, isError: volumesError } = useVolumes(tenantId);
  const { data: secretsData, isLoading: secretsLoading, isError: secretsError } = useSecrets(tenantId);
  const { data: connectionsData, isLoading: connectionsLoading, isError: connectionsError } = useDatabaseConnections(tenantId);
  const { data: documentDbsData, isLoading: documentDbsLoading, isError: documentDbsError } = useDocumentDatabases(tenantId);
  const { data: managedDbsData, isLoading: managedDbsLoading, isError: managedDbsError } = useManagedSqlDbs(tenantId);

  // Transform data into ResourceOption format
  const applications = useMemo(() => {
    if (!applicationsData) return [];
    return applicationsData.map((app) => ({
      code: app.applicationCode,
      name: app.applicationName,
      id: app.applicationId,
    }));
  }, [applicationsData]);

  const volumes = useMemo(() => {
    if (!volumesData) return [];
    return volumesData.map((v) => ({
      code: v.volumeCode,
      name: v.volumeName,
      id: v.volumeId,
    }));
  }, [volumesData]);

  const secrets = useMemo(() => {
    if (!secretsData) return [];
    return secretsData.map((s) => ({
      code: s.secretCode,
      name: s.secretName,
      id: s.secretId,
    }));
  }, [secretsData]);

  const connections = useMemo(() => {
    if (!connectionsData) return [];
    return connectionsData.map((c) => ({
      code: c.dbConnectionCode,
      name: c.dbConnectionName,
      id: c.dbConnectionId,
    }));
  }, [connectionsData]);

  const documentDbs = useMemo(() => {
    if (!documentDbsData) return [];
    return documentDbsData.map((db) => ({
      code: db.documentDBCode,
      name: db.documentDBName,
      id: db.documentDBId,
    }));
  }, [documentDbsData]);

  const managedDbs = useMemo(() => {
    if (!managedDbsData) return [];
    return managedDbsData.map((db) => ({
      code: db.managedSqlDbCode,
      name: db.managedSqlDbName,
      id: db.managedSqlDbId,
    }));
  }, [managedDbsData]);

  const isLoading = appsLoading || volumesLoading || secretsLoading || connectionsLoading || documentDbsLoading || managedDbsLoading;
  const isError = appsError || volumesError || secretsError || connectionsError || documentDbsError || managedDbsError;

  return {
    applications,
    volumes,
    secrets,
    connections,
    documentDbs,
    managedDbs,
    isLoading,
    isError,
  };
}
